// src/components/TwoFactorSettings.js
// Double authentification (TOTP) : activation par QR code, codes de secours, désactivation.
// Facultative pour tous, obligatoire pour les administrateurs.
import { useState } from 'react';
import axios from 'axios';
import QRCode from 'qrcode';
import { toast } from 'sonner';
import { ShieldCheck, Smartphone, KeyRound, Copy, Download, Loader2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const API_URL = 'https://cyberguard-api-ka8f.onrender.com';
const inputCls = 'w-full bg-black border border-zinc-800 p-3 text-white font-mono text-sm focus:border-blue-500 outline-none transition-all placeholder:text-zinc-700';
const btnCls = 'inline-flex items-center justify-center gap-2 px-4 py-2.5 font-mono text-xs uppercase tracking-widest disabled:opacity-50';

function RecoveryCodes({ codes, onDone }) {
  const text = codes.join('\n');
  const copy = async () => {
    try { await navigator.clipboard.writeText(text); toast.success('Codes copiés'); }
    catch { toast.error('Copie impossible, sélectionnez les codes à la main'); }
  };
  const download = () => {
    const url = URL.createObjectURL(new Blob([`Codes de secours CyberGuard\n\n${text}\n`], { type: 'text/plain' }));
    const a = document.createElement('a');
    a.href = url; a.download = 'cyberguard-codes-de-secours.txt'; a.click();
    URL.revokeObjectURL(url);
  };
  return (
    <div className="space-y-4" data-testid="recovery-codes">
      <p className="text-zinc-300 text-sm">
        Gardez ces codes de secours en lieu sûr (gestionnaire de mots de passe, papier). Chacun permet de vous connecter
        une fois si vous perdez votre téléphone. <strong className="text-white">Ils ne seront plus affichés.</strong>
      </p>
      <div className="grid grid-cols-2 gap-2 bg-black border border-zinc-800 p-4">
        {codes.map((c) => <code key={c} className="font-mono text-sm text-zinc-200">{c}</code>)}
      </div>
      <div className="flex flex-wrap gap-2">
        <button onClick={copy} className={`${btnCls} border border-zinc-700 hover:border-blue-500 text-zinc-300`}><Copy className="w-3 h-3" /> Copier</button>
        <button onClick={download} className={`${btnCls} border border-zinc-700 hover:border-blue-500 text-zinc-300`}><Download className="w-3 h-3" /> Télécharger</button>
        <button onClick={onDone} className={`${btnCls} bg-blue-600 hover:bg-blue-700 text-white`}>C'est noté</button>
      </div>
    </div>
  );
}

export default function TwoFactorSettings() {
  const { user, token, startSession } = useAuth();
  const [setup, setSetup] = useState(null); // { secret, svg }
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [codes, setCodes] = useState(null);
  const [mode, setMode] = useState(null); // 'disable' | 'regenerate'
  const [busy, setBusy] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };
  const enabled = !!user?.totp_enabled;
  const isAdmin = !!user?.is_admin;

  const begin = async () => {
    setBusy(true);
    try {
      const res = await axios.post(`${API_URL}/api/auth/2fa/setup`, {}, { headers });
      const svg = await QRCode.toString(res.data.otpauth_uri, { type: 'svg', margin: 1, color: { dark: '#000000', light: '#ffffff' } });
      setSetup({ secret: res.data.secret, svg });
      setCode('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Impossible de démarrer la configuration');
    } finally {
      setBusy(false);
    }
  };

  const enable = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await axios.post(`${API_URL}/api/auth/2fa/enable`, { code: code.trim() }, { headers });
      setCodes(res.data.recovery_codes);
      setSetup(null);
      setCode('');
      await startSession(res.data.token);
      toast.success('Double authentification activée');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Code incorrect');
    } finally {
      setBusy(false);
    }
  };

  const disable = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await axios.post(`${API_URL}/api/auth/2fa/disable`, { password, code: code.trim() }, { headers });
      await startSession(res.data.token);
      setMode(null); setPassword(''); setCode('');
      toast.success('Double authentification désactivée');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    } finally {
      setBusy(false);
    }
  };

  const regenerate = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await axios.post(`${API_URL}/api/auth/2fa/recovery-codes`, { code: code.trim() }, { headers });
      setCodes(res.data.recovery_codes);
      setMode(null); setCode('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Code incorrect');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-6 space-y-5" data-testid="two-factor">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h2 className="text-white font-mono font-bold text-xs uppercase tracking-widest border-l-2 border-purple-500 pl-3 flex items-center gap-2">
          <ShieldCheck className="w-3 h-3 text-purple-500" /> Double authentification
        </h2>
        <span className={`text-[10px] font-mono uppercase px-2 py-0.5 border ${enabled ? 'border-green-500/40 text-green-400' : 'border-zinc-700 text-zinc-500'}`}>
          {enabled ? 'Activée' : 'Désactivée'}
        </span>
      </div>

      {codes ? (
        <RecoveryCodes codes={codes} onDone={() => setCodes(null)} />
      ) : setup ? (
        <form onSubmit={enable} className="space-y-4">
          <ol className="text-zinc-300 text-sm space-y-1 list-decimal pl-5">
            <li>Installez une application d'authentification (Google Authenticator, Microsoft Authenticator, 1Password, Bitwarden…).</li>
            <li>Scannez ce QR code avec l'application.</li>
            <li>Saisissez le code à 6 chiffres qu'elle affiche.</li>
          </ol>
          <div className="flex flex-col sm:flex-row gap-5 items-start">
            <div className="w-44 h-44 bg-white p-1 shrink-0" aria-label="QR code de configuration" dangerouslySetInnerHTML={{ __html: setup.svg }} />
            <div className="space-y-2 min-w-0">
              <p className="text-zinc-500 text-xs">Impossible de scanner ? Saisissez cette clé dans l'application :</p>
              <code className="block break-all bg-black border border-zinc-800 px-2 py-1 text-xs font-mono text-zinc-200" data-testid="totp-secret">{setup.secret}</code>
            </div>
          </div>
          <input value={code} onChange={(e) => setCode(e.target.value)} inputMode="numeric" autoComplete="one-time-code"
            placeholder="123456" className={`${inputCls} max-w-xs text-lg tracking-widest`} aria-label="Code à 6 chiffres" />
          <div className="flex gap-2">
            <button type="submit" disabled={busy || code.trim().length < 6} className={`${btnCls} bg-blue-600 hover:bg-blue-700 text-white`}>
              {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <ShieldCheck className="w-3 h-3" />} Activer
            </button>
            <button type="button" onClick={() => setSetup(null)} className={`${btnCls} border border-zinc-700 text-zinc-400 hover:text-white`}>Annuler</button>
          </div>
        </form>
      ) : !enabled ? (
        <div className="space-y-4">
          <p className="text-zinc-400 text-sm">
            En plus du mot de passe, un code à 6 chiffres généré par votre téléphone est demandé à chaque connexion.
            Quelqu'un qui obtiendrait votre mot de passe ne pourrait pas accéder à votre compte.
            {isAdmin && <span className="block text-amber-400 mt-2">Obligatoire pour accéder à l'administration.</span>}
          </p>
          <button onClick={begin} disabled={busy} className={`${btnCls} bg-blue-600 hover:bg-blue-700 text-white`}>
            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Smartphone className="w-3 h-3" />} Activer la double authentification
          </button>
        </div>
      ) : mode ? (
        <form onSubmit={mode === 'disable' ? disable : regenerate} className="space-y-3 max-w-sm">
          {mode === 'disable' && (
            <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Mot de passe"
              autoComplete="current-password" className={inputCls} aria-label="Mot de passe" />
          )}
          <input value={code} onChange={(e) => setCode(e.target.value)} placeholder="Code à 6 chiffres ou code de secours"
            autoComplete="one-time-code" className={inputCls} aria-label="Code de vérification" />
          <div className="flex gap-2">
            <button type="submit" disabled={busy || !code.trim() || (mode === 'disable' && !password)}
              className={`${btnCls} ${mode === 'disable' ? 'bg-red-600/80 hover:bg-red-600' : 'bg-blue-600 hover:bg-blue-700'} text-white`}>
              {busy && <Loader2 className="w-3 h-3 animate-spin" />} {mode === 'disable' ? 'Désactiver' : 'Générer de nouveaux codes'}
            </button>
            <button type="button" onClick={() => { setMode(null); setCode(''); setPassword(''); }} className={`${btnCls} border border-zinc-700 text-zinc-400 hover:text-white`}>Annuler</button>
          </div>
        </form>
      ) : (
        <div className="space-y-4">
          <p className="text-zinc-400 text-sm">Un code de votre application d'authentification est demandé à chaque connexion.</p>
          <div className="flex flex-wrap gap-2">
            <button onClick={() => setMode('regenerate')} className={`${btnCls} border border-zinc-700 hover:border-blue-500 text-zinc-300`}>
              <KeyRound className="w-3 h-3" /> Nouveaux codes de secours
            </button>
            {isAdmin ? (
              <span className="text-zinc-500 text-xs self-center">Obligatoire pour les comptes administrateurs.</span>
            ) : (
              <button onClick={() => setMode('disable')} className={`${btnCls} border border-red-500/30 text-red-400 hover:bg-red-600/10`}>Désactiver</button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
